import PropTypes from 'prop-types'
import { PlusCircle, Info, Trash, Check } from '@phosphor-icons/react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useState } from 'react'

import ModalContainer from '../../../../ModalContainer/ModelContainer'
import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import { AmenityImageUpload } from '../components/amenity-image-upload'
import API from '../../../../../api/api'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'

const makeValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().required(t('reservations:amenityForms.spaceModal.nameRequired')),
    image: Yup.string().required(t('reservations:amenityForms.spaceModal.imageRequired')),
  })

const SpaceModal = ({
  onClose = () => {},
  onSuccess = () => {},
  selectedLocation = null,
  prefillData = null,
}) => {
  const { t } = useTranslation(['reservations', 'common'])
  const [isDeleting, setIsDeleting] = useState(false)
  const { amenityId } = useParams()
  const { displaySnackbar } = useSnackbar()
  const isReadOnly = !selectedLocation && prefillData !== null

  const onCreateLocation = async (values) => {
    try {
      await API.createAmenityLocation(amenityId, values)
      onSuccess()
      onClose()
      displaySnackbar(t('reservations:amenityForms.spaceModal.createdSuccessfully'), false)
    } catch (error) {
      console.error('Error creating space', error)
      displaySnackbar(t('reservations:amenityForms.spaceModal.errorCreating'), true)
    }
  }

  const onUpdateLocation = async (values) => {
    try {
      await API.updateAmenityLocation(selectedLocation.id, values)
      onSuccess()
      onClose()
      displaySnackbar(t('reservations:amenityForms.spaceModal.updatedSuccessfully'), false)
    } catch (error) {
      console.error('Error updating space', error)
      displaySnackbar(t('reservations:amenityForms.spaceModal.errorUpdating'), true)
    }
  }

  const onDeleteLocation = async () => {
    try {
      setIsDeleting(true)
      await API.deleteAmenityLocation(selectedLocation.id)
      onSuccess()
      onClose()
      displaySnackbar(t('reservations:amenityForms.spaceModal.deletedSuccessfully'), false)
    } catch (error) {
      const message =
        error?.response?.data?.message || t('reservations:amenityForms.spaceModal.errorDeleting')
      console.error('Error deleting space', message)
      displaySnackbar(message, true)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <ModalContainer
      onClose={onClose}
      headerTitle={
        selectedLocation
          ? t('reservations:amenityForms.spaceModal.editAmenitySpace')
          : t('reservations:amenityForms.spaces.addASpace')
      }
      additionalClass="spaces-modal"
    >
      <div className="spaces-modal-content">
        <Formik
          initialValues={{
            name: selectedLocation?.name || prefillData?.name || '',
            description: selectedLocation?.description || prefillData?.description || '',
            image: selectedLocation?.image || prefillData?.image || '',
            available: selectedLocation?.available ?? true,
          }}
          validationSchema={makeValidationSchema(t)}
          onSubmit={selectedLocation ? onUpdateLocation : onCreateLocation}
          enableReinitialize
        >
          {({
            handleSubmit,
            handleChange,
            setFieldTouched,
            setFieldValue,
            isSubmitting,
            values,
            errors,
            touched,
            isValid,
            dirty,
          }) => (
            <Form className="spaces-modal-form" onSubmit={handleSubmit}>
              <div className="spaces-modal-form-body">
                <div className="form-step__row">
                  <AmenityImageUpload
                    image={values.image}
                    onChange={(image) => setFieldValue('image', image)}
                    error={errors.image}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="form-step__row">
                  <TextField
                    name="name"
                    label={t('reservations:amenityForms.spaceModal.spaceNameRequired')}
                    placeholder={t('reservations:amenityForms.spaceModal.enterNamePlaceholder')}
                    value={values.name}
                    onChange={handleChange}
                    isError={touched.name && !!errors.name}
                    onBlur={() => setFieldTouched('name')}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="form-step__row">
                  <TextAreaField
                    name="description"
                    label={t('reservations:amenityForms.spaceModal.descriptionLabel')}
                    placeholder={t('reservations:amenityForms.spaceModal.describeSpacePlaceholder')}
                    value={values.description}
                    onChange={handleChange}
                    isError={touched.description && !!errors.description}
                    onBlur={() => setFieldTouched('description')}
                    rows={4}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="form-step__row form-step__row--between">
                  <div>
                    <div className="form-step__label">
                      {t('reservations:amenitiesTable.availableForBooking')}
                      <Info size={16} data-tip data-for="availableForBooking" />
                      <ReactTooltip
                        className="tooltip"
                        multiline={true}
                        id="availableForBooking"
                        place="top"
                        effect="solid"
                      >
                        {t('reservations:amenityForms.spaceModal.availableForBookingTooltip')}
                      </ReactTooltip>
                    </div>
                  </div>
                  <div>
                    <ToggleField
                      name="available"
                      label={
                        values.available
                          ? t('reservations:amenityForms.yes')
                          : t('reservations:amenityForms.no')
                      }
                      value={values.available}
                      onChange={handleChange}
                      labelOnRight
                    />
                  </div>
                </div>
              </div>
              <div className="spaces-modal-actions">
                <div>
                  {selectedLocation && (
                    <button
                      className="button button-red"
                      type="button"
                      onClick={onDeleteLocation}
                      disabled={isSubmitting || isDeleting}
                    >
                      <Trash />
                    </button>
                  )}
                </div>
                <div>
                  <button type="button" className="button button-gray" onClick={onClose}>
                    {t('common:actions.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="button button-primary"
                    disabled={
                      selectedLocation
                        ? !isValid || !dirty || isSubmitting
                        : Object.keys(errors).length > 0 || isSubmitting
                    }
                  >
                    {selectedLocation ? <Check size={16} /> : <PlusCircle size={16} />}
                    {selectedLocation
                      ? t('common:actions.save')
                      : t('reservations:amenityForms.spaces.addASpace')}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </ModalContainer>
  )
}

export default SpaceModal

SpaceModal.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  selectedLocation: PropTypes.object,
  prefillData: PropTypes.object,
}
